/**
 * Módulo de Licitaciones para Administradores
 * Muestra todas las propuestas de proveedores de la tabla SICETAC_TARIFAS_PROVEEDORES
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FileText, Download, Filter, RefreshCw } from 'lucide-react';
import apiConfig from '../../config/api.config';

const Container = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
`;

const ControlsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
`;

const FilterBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  flex: 1;
  min-width: 180px;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Select = styled.select`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.gray300};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.white};
  color: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.white : theme.colors.textPrimary};
  border: 2px solid ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    background: ${({ theme, variant }) => 
      variant === 'primary' ? theme.colors.primaryDark : theme.colors.gray50};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  flex: 1;
  min-width: 200px;
`;

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatValue = styled.div`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
`;

const TableContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: ${({ theme }) => theme.colors.gray50};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray200};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Tr = styled.tr`
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
  }
`;

const Badge = styled.span<{ color: string }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background: ${({ color }) => color}20;
  color: ${({ color }) => color};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SpinIcon = styled.div`
  display: inline-block;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

interface Licitacion {
  proveedor: string;
  nombre_proveedor: string;
  origen_ciudad: string;
  destino_ciudad: string;
  tipo_vehiculo: string;
  tipo_carga: string;
  configuracion: string;
  valor_sicetac: number;
  valor_de_tarifa: number;
  porcentaje: number;
  comentarios: string;
}

const LicitacionesModule: React.FC = () => {
  const [licitaciones, setLicitaciones] = useState<Licitacion[]>([]);
  const [filteredLicitaciones, setFilteredLicitaciones] = useState<Licitacion[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filterProveedor, setFilterProveedor] = useState('');
  const [filterOrigen, setFilterOrigen] = useState('');
  const [filterDestino, setFilterDestino] = useState('');
  const [filterVehiculo, setFilterVehiculo] = useState('');
  const [filterCarga, setFilterCarga] = useState('');

  const fetchLicitaciones = async () => {
    setLoading(true);
    try {
      const response = await fetch(apiConfig.endpoints.licitaciones);
      const data = await response.json();
      
      if (response.ok) {
        setLicitaciones(data.licitaciones);
        setFilteredLicitaciones(data.licitaciones);
      } else {
        console.error('Error al cargar licitaciones:', data.error);
      }
    } catch (error) {
      console.error('Error al cargar licitaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicitaciones();
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let filtered = licitaciones;

    if (filterProveedor) {
      filtered = filtered.filter(lic => lic.proveedor === filterProveedor);
    }
    if (filterOrigen) {
      filtered = filtered.filter(lic => lic.origen_ciudad === filterOrigen);
    }
    if (filterDestino) {
      filtered = filtered.filter(lic => lic.destino_ciudad === filterDestino);
    }
    if (filterVehiculo) {
      filtered = filtered.filter(lic => lic.tipo_vehiculo === filterVehiculo);
    }
    if (filterCarga) {
      filtered = filtered.filter(lic => lic.tipo_carga === filterCarga);
    }

    setFilteredLicitaciones(filtered);
  }, [filterProveedor, filterOrigen, filterDestino, filterVehiculo, filterCarga, licitaciones]);

  // Obtener listas únicas para los filtros (usar nombre_proveedor para mostrar, proveedor para filtrar)
  const proveedoresMap = licitaciones.reduce((acc, l) => {
    acc[l.proveedor] = l.nombre_proveedor || l.proveedor;
    return acc;
  }, {} as Record<string, string>);
  const proveedores = Array.from(new Set(licitaciones.map(l => l.proveedor))).sort();
  const origenes = Array.from(new Set(licitaciones.map(l => l.origen_ciudad))).sort();
  const destinos = Array.from(new Set(licitaciones.map(l => l.destino_ciudad))).sort();
  const vehiculos = Array.from(new Set(licitaciones.map(l => l.tipo_vehiculo))).sort();
  const cargas = Array.from(new Set(licitaciones.map(l => l.tipo_carga))).sort();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const clearFilters = () => {
    setFilterProveedor('');
    setFilterOrigen('');
    setFilterDestino('');
    setFilterVehiculo('');
    setFilterCarga('');
  };

  const exportToCSV = () => {
    if (filteredLicitaciones.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Crear encabezados
    const headers = [
      'Nombre del Proveedor',
      'Usuario',
      'Origen',
      'Destino',
      'Tipo Vehículo',
      'Tipo Carga',
      'Configuración',
      'Valor SICETAC',
      'Tarifa Propuesta',
      '% Margen',
      'Comentarios'
    ];

    // Crear filas de datos
    const rows = filteredLicitaciones.map(lic => [
      lic.nombre_proveedor || lic.proveedor,
      lic.proveedor,
      lic.origen_ciudad,
      lic.destino_ciudad,
      lic.tipo_vehiculo,
      lic.tipo_carga,
      lic.configuracion,
      lic.valor_sicetac,
      lic.valor_de_tarifa,
      lic.porcentaje,
      lic.comentarios || ''
    ]);

    // Combinar encabezados y filas
    const csvContent = [
      headers.join(','),
      ...rows.map(row => 
        row.map(cell => {
          // Escapar comillas y comas en el contenido
          const cellStr = String(cell).replace(/"/g, '""');
          return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
            ? `"${cellStr}"` 
            : cellStr;
        }).join(',')
      )
    ].join('\n');

    // Crear archivo y descargar
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fecha = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `licitaciones_${fecha}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container>
      <Header>
        <Title>
          <FileText size={32} />
          Licitaciones de Proveedores
        </Title>
        <Subtitle>
          Gestión y visualización de todas las propuestas de licitación - Total: {filteredLicitaciones.length} de {licitaciones.length}
        </Subtitle>
      </Header>

      <ControlsBar>
        <FilterBox>
          <FilterLabel>Proveedor:</FilterLabel>
          <Select 
            value={filterProveedor} 
            onChange={(e) => setFilterProveedor(e.target.value)}
          >
            <option value="">Todos</option>
            {proveedores.map(p => (
              <option key={p} value={p}>{proveedoresMap[p]}</option>
            ))}
          </Select>
        </FilterBox>

        <FilterBox>
          <FilterLabel>Origen:</FilterLabel>
          <Select 
            value={filterOrigen} 
            onChange={(e) => setFilterOrigen(e.target.value)}
          >
            <option value="">Todas</option>
            {origenes.map(o => (
              <option key={o} value={o}>{o}</option>
            ))}
          </Select>
        </FilterBox>

        <FilterBox>
          <FilterLabel>Destino:</FilterLabel>
          <Select 
            value={filterDestino} 
            onChange={(e) => setFilterDestino(e.target.value)}
          >
            <option value="">Todas</option>
            {destinos.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Select>
        </FilterBox>

        <FilterBox>
          <FilterLabel>Vehículo:</FilterLabel>
          <Select 
            value={filterVehiculo} 
            onChange={(e) => setFilterVehiculo(e.target.value)}
          >
            <option value="">Todos</option>
            {vehiculos.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </Select>
        </FilterBox>

        <FilterBox>
          <FilterLabel>Carga:</FilterLabel>
          <Select 
            value={filterCarga} 
            onChange={(e) => setFilterCarga(e.target.value)}
          >
            <option value="">Todas</option>
            {cargas.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </FilterBox>

        <Button onClick={clearFilters} variant="secondary">
          <Filter size={18} />
          Limpiar
        </Button>

        <Button onClick={fetchLicitaciones} disabled={loading}>
          <RefreshCw size={18} />
          Actualizar
        </Button>

        <Button variant="secondary" onClick={exportToCSV}>
          <Download size={18} />
          Exportar
        </Button>
      </ControlsBar>

      <TableContainer>
        {loading ? (
          <LoadingContainer>
            <SpinIcon>
              <RefreshCw size={24} />
            </SpinIcon>
            Cargando licitaciones...
          </LoadingContainer>
        ) : filteredLicitaciones.length === 0 ? (
          <EmptyState>
            {(filterProveedor || filterOrigen || filterDestino || filterVehiculo || filterCarga) ? (
              <>No se encontraron licitaciones con los filtros seleccionados</>
            ) : (
              <>No hay licitaciones registradas</>
            )}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Nombre del Proveedor</Th>
                <Th>Origen</Th>
                <Th>Destino</Th>
                <Th>Vehículo</Th>
                <Th>Carga</Th>
                <Th>Configuración</Th>
                <Th>Valor SICETAC</Th>
                <Th>Tarifa Propuesta</Th>
                <Th>% Margen</Th>
                <Th>Comentarios</Th>
              </tr>
            </thead>
            <tbody>
              {filteredLicitaciones.map((lic, index) => (
                <Tr key={index}>
                  <Td>
                    <strong>{lic.nombre_proveedor || lic.proveedor}</strong>
                  </Td>
                  <Td>{lic.origen_ciudad}</Td>
                  <Td>{lic.destino_ciudad}</Td>
                  <Td>
                    <Badge color="#3b82f6">{lic.tipo_vehiculo}</Badge>
                  </Td>
                  <Td>{lic.tipo_carga}</Td>
                  <Td>{lic.configuracion}</Td>
                  <Td>{formatCurrency(lic.valor_sicetac)}</Td>
                  <Td>
                    <strong>{formatCurrency(lic.valor_de_tarifa)}</strong>
                  </Td>
                  <Td>
                    <Badge color={lic.porcentaje > 0 ? '#10b981' : '#ef4444'}>
                      {lic.porcentaje > 0 ? '+' : ''}{lic.porcentaje.toFixed(1)}%
                    </Badge>
                  </Td>
                  <Td>
                    <div style={{ fontSize: '0.85rem', maxWidth: '200px' }}>
                      {lic.comentarios || '-'}
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>
    </Container>
  );
};

export default LicitacionesModule;

